"use client";

import { useCallback, useEffect, useState } from "react";
import {
  ANALYTICS_CONSENT_CHANGED_EVENT,
  type AnalyticsConsent,
  getAnalyticsConsent,
} from "@/lib/analytics-consent";
import { applyAnalyticsConsent } from "@/lib/posthog-browser";
import { reportConsentChoice } from "@/lib/report-consent-choice";
import type { AnalyticsConsentSource } from "@/lib/consent-record";

export function useAnalyticsConsent() {
  const [consent, setConsent] = useState<AnalyticsConsent | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const sync = () => {
      setConsent(getAnalyticsConsent());
      setIsReady(true);
    };

    sync();
    window.addEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const accept = useCallback((source: AnalyticsConsentSource = "cookie_banner") => {
    applyAnalyticsConsent("accepted");
    reportConsentChoice({
      kind: "analytics",
      choice: "accepted",
      source,
    });
  }, []);

  const reject = useCallback((source: AnalyticsConsentSource = "cookie_banner") => {
    applyAnalyticsConsent("rejected");
    reportConsentChoice({
      kind: "analytics",
      choice: "rejected",
      source,
    });
  }, []);

  return {
    consent,
    isReady,
    shouldShowBanner: isReady && consent === null,
    accept,
    reject,
  };
}
